import { Router, ScreenBase } from "@frui.ts/screens";
import { validityDays } from "entities/chargeValidity";
import FuelType from "entities/fuelType";
import { action, computed } from "mobx";
import ChargeTypesService from "services/chargeTypesService";
import LocalizationService from "services/localizationService";

export const PriceListModuleNavigationName = "price_list";

@Router.registerRoute({ route: PriceListModuleNavigationName, name: "price_list" })
export default class PriceListViewModel extends ScreenBase {
  constructor(private chargeTypesService: ChargeTypesService, private localizationService: LocalizationService) {
    super();
  }

  protected async onInitialize(): Promise<Promise<any> | void> {
    await this.chargeTypesService.startInitialization();
    return super.onInitialize();
  }

  @computed
  get isInitialized() {
    return this.chargeTypesService.isInitialized;
  }

  @computed
  get validSinceDate() {
    return this.chargeTypesService.chargePriceList?.validSinceDate
      ? this.localizationService.formatDate(this.chargeTypesService.chargePriceList?.validSinceDate)
      : null;
  }

  @computed
  get isCSLocale() {
    return this.localizationService.locale === "cs";
  }

  @computed
  get fuelTypes() {
    const sortedFuelTypes = this.chargeTypesService.getAvailableFuelTypesSorted();

    //edaz-task #1167
    sortedFuelTypes.push({
      fuelType: FuelType.Electromobile,
      id: Math.random().toString(),
      isDeleted: false,
      name: this.localizationService.translateAttribute("fuelType", "ELECTROMOBILE"),
    });

    return sortedFuelTypes;
  }

  formatPrice(price: number) {
    return this.localizationService.formatPrice(price);
  }

  @computed
  get translateTitle() {
    return this.localizationService.translate(`general.validPriceList`).replace("%VALID_SINCE%", `${this.validSinceDate}`);
  }

  translateFuelType(fuelType: FuelType) {
    return this.localizationService.translateAttribute("fuelType", fuelType);
  }

  fuelTypeDescription(fuelType: FuelType) {
    if (fuelType === FuelType.NaturalGas || fuelType === FuelType.PhevWithLimitedCo2) {
      return this.localizationService.translateAttribute("fuelType", `${fuelType}_DESCRIPTION`);
    }
    if (fuelType === FuelType.Electromobile) {
      return this.localizationService.translateAttribute("fuelType", `${fuelType}_DESCRIPTION`, undefined, true);
    }

    return undefined;
  }

  isDescriptionTooLong(fuelType: FuelType) {
    const description = this.fuelTypeDescription(fuelType);
    if (!description) {
      return false;
    }
    // Arbitrary amount, just used a number that would go over 2 lines
    return description.length > 40;
  }

  @action.bound
  chargeTypesWithPrices() {
    return this.chargeTypesService.validChargeTypes
      .sort((a, b) => {
        return validityDays(b.validFor) - validityDays(a.validFor);
      })
      .filter((x) => this.chargeTypesService.hasPrices(x))
      .map((x) => this.chargeTypesService.getPricesWithFuelTypes(x));
  }
}
